import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Grid, makeStyles, TextField, Typography } from '@material-ui/core';

import useBenchmarkSearch, {
  SET_SERIES_TYPE_ID,
  SET_SERIES_TYPE_DETAIL_ID,
  SET_PERIODICITY_ID,
  SET_IS_ACTIVE,
  SET_PARENT_OBJECT_TYPE_ID,
  SET_SEARCH_CRITERIA
} from 'hooks/useBenchmarkSearch';
import CheckboxButton from 'components/buttons/CheckboxButton';
import LoadingSpinner from 'components/LoadingSpinner';
import { ThemeButton, Message } from 'components/shared';

import AdvancedSearchDropdown from './AdvancedSearchDropdown';
import LOOKUP_TYPES from '../utils/lookupTypes';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: '5px'
  },
  errorBox: {
    width: '287px', 
    '& span': {
      overflow: 'auto',
      textOverflow: 'unset',
      whiteSpace: 'none'
    }
  },
  field: {
    margin: '0'
  },
  fieldHeader: {
    fontSize: '0.7rem',
    color: theme.palette.grey.dark,
    fontStyle: 'italic'
  },
  input: {
    padding: '0 4px',
    color: theme.palette.grey.dark,
    border: `1px solid ${theme.palette.grey.medium}`,
    borderRadius: '4px',
    marginBottom: '8px',
    '&:before': {
      display: 'none'
    },
    '&:after': {
      display: 'none'
    },
    '&:hover': {
      borderColor: theme.palette.accent.main,
      color: theme.palette.accent.main
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.light
    }
  },
  searchPanel: {
    flexGrow: 1,
    padding: '0 8px'
  },
  searchField: {
    flexGrow: '1',
    marginRight: '10px'
  },
  searchInput: {
    padding: '0 4px',
    color: theme.palette.grey.dark,
    border: `1px solid ${theme.palette.grey.medium}`,
    borderRadius: '4px',
    '&:before': {
      display: 'none'
    },
    '&:after': {
      display: 'none'
    },
    '&:hover': {
      borderColor: theme.palette.accent.main,
      color: theme.palette.accent.main
    },
    '&:focus-within': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.primary.light
    }
  },
  results: {
    ...theme.scrollStyles,
    overflow: 'auto',
    marginTop: '5px',
    height: '135px',
    border: `1px solid ${theme.palette.grey.medium}`,
    borderRadius: '4px'
  },
  resultPlaceholder: {
    color: theme.palette.grey.dark,
    fontSize: '0.8rem',
    fontStyle: 'italic'
  },
  resultItem: {
    display: 'flex',
    color: theme.palette.grey.dark,
    padding: '3px 0',
    alignItems: 'center',
    cursor: 'pointer',
    '& p': {
      fontSize: '0.8rem'
    }
  },
  highlighted: {
    backgroundColor: theme.palette.primary.light,
    '& p': {
      color: theme.palette.primary.main
    }
  },
  cancelButton: {
    width: '100%',
    color: theme.palette.primary.main,
    margin: '5px 0 0 2px'
  },
  acceptButton: {
    width: '100%',
    marginLeft: '2px',
    '&:disabled': {
      backgroundColor: theme.palette.primary.disabled,
      color: theme.palette.white
    }
  }
}));

// TODO: Improve code quality
const AdvancedSearch = ({ onAccepted, clearAdvancedView }) => {
  const classes = useStyles();
  const [state, dispatch, search, results, apiState] = useBenchmarkSearch();
  const [highlightedIndex, setHighlightedIndex] = useState();

  const acceptBenchmark = () => {
    const item = results[highlightedIndex];
    onAccepted(item);
  };

  const highlightItem = resultIndex => {
    setHighlightedIndex(resultIndex);
  };

  const onSearchCriteriaChange = e => {
    dispatch({ type: SET_SEARCH_CRITERIA, payload: e.target.value });
  };

  const onSeriesTypeIdChange = e => {
    dispatch({ type: SET_SERIES_TYPE_ID, payload: Number(e.target.value) });
  };

  const onSeriesTypeDetailIdChange = e => {
    dispatch({ type: SET_SERIES_TYPE_DETAIL_ID, payload: Number(e.target.value) });
  };

  const onPeriodicityIdChange = e => {
    dispatch({ type: SET_PERIODICITY_ID, payload: Number(e.target.value) });
  };

  const onParentObjectTypeIdChange = e => {
    dispatch({ type: SET_PARENT_OBJECT_TYPE_ID, payload: Number(e.target.value) });
  };

  const onIsActiveChange = e => {
    dispatch({ type: SET_IS_ACTIVE, payload: e.value });
  };

  const onKeyPress = e => {
    if (e.key === 'Enter') {
      search();
    }
  };

  const getResultClasses = resultIndex => {
    const classList = ['resultItem'];
    if (highlightedIndex === resultIndex) {
      classList.push('highlighted');
    }
    return classList.map(c => classes[c]).join(' ');
  };

  const renderResults = () => {
    if (apiState.isLoading) {
      return <LoadingSpinner />;
    }
    if (apiState.isError && apiState.error) {
      return  <div className={classes.errorBox}><Message type="error" text={apiState.error} /></div>;
    }
    if (!results.length) {
      return <Typography className={classes.resultPlaceholder}>No search results</Typography>;
    }

    return results.map((benchmark, resultIndex) => (
      <Box
        key={benchmark.timeSerieId}
        onClick={() => highlightItem(resultIndex)}
        className={getResultClasses(resultIndex)}
      >
        <Typography>{benchmark.name}</Typography>
      </Box>
    ));
  };

  return (
    <Box className={classes.searchPanel}>
      <Grid container direction="row" spacing={1}>
        <Grid container item xs={9}>
          <TextField
            className={classes.searchField}
            value={state.searchCriteria}
            onKeyPress={onKeyPress}
            onChange={onSearchCriteriaChange}
            InputProps={{ className: classes.searchInput }}
          />
        </Grid>
        <Grid item xs={3}>
          <ThemeButton themeColor="accent" size="small" onClick={search}>
            Search
          </ThemeButton>
        </Grid>
      </Grid>

      <Box className={classes.root}>
        <Grid container direction="row" alignItems="center" spacing={1}>
          <AdvancedSearchDropdown
            type={LOOKUP_TYPES.seriesType}
            label="Series Type Id"
            value={state.seriesTypeId}
            onChange={onSeriesTypeIdChange}
          />
          <AdvancedSearchDropdown
            type={LOOKUP_TYPES.seriesTypeDetail}
            label="Series Type Detail Id"
            value={state.seriesTypeDetailId}
            onChange={onSeriesTypeDetailIdChange}
          />
          <AdvancedSearchDropdown
            type={LOOKUP_TYPES.periodicity}
            label="Periodicity Id"
            value={state.periodicityId}
            onChange={onPeriodicityIdChange}
          />
        </Grid>
        <Grid container direction="row" alignItems="flex-end" justify="flex-start" spacing={1}>
          <AdvancedSearchDropdown
            type={LOOKUP_TYPES.parentObjectType}
            label="Parent Object Type Id"
            value={state.parentObjectTypeId}
            onChange={onParentObjectTypeIdChange}
          />
          <Grid className={classes.field} item xs={4}>
            <CheckboxButton
              hasTransparentBg
              title="Is Active"
              checked={state.isActive}
              onChange={onIsActiveChange}
            />
          </Grid>
        </Grid>
      </Box>

      <Grid container direction="row" spacing={1} className={classes.subSection}>
        <Grid className={classes.results} item xs={9}>
          {renderResults()}
        </Grid>
        <Grid container item direction="column" justify="flex-end" xs={3}>
          <Button
            disableElevation
            variant="contained"
            color="primary"
            className={classes.acceptButton}
            disabled={highlightedIndex === undefined}
            onClick={acceptBenchmark}
          >
            Accept
          </Button>
          <Button color="primary" className={classes.cancelButton} onClick={clearAdvancedView}>
            Cancel
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

AdvancedSearch.propTypes = {
  onAccepted: PropTypes.func.isRequired,
  clearAdvancedView: PropTypes.func.isRequired
};

export default AdvancedSearch;
